import { Link } from "react-router-dom";
import { MapPin, Clock, Briefcase, Bookmark, BookmarkCheck } from "lucide-react";
import type { JobPostingListItem } from "@/services/api/types";
import { Pill } from "./Pill";
import { employmentLabel, workplaceLabel, seniorityLabel, stripMarkdown, monogram, isRecent } from "./jobCardUtils";

/**
 * One row on the careers board, in the dcv2 design language.
 *
 * The card is the canvas's capability card: paper2 on a .13 hairline, radius
 * 18, lifted by `dc-h-card`. Featured swaps the hairline for the accent and
 * takes the larger padding, which is how the canvas marks a privileged item
 * (step 04's 2px accent rule) rather than by inventing a second surface.
 *
 * The three metadata icons stay because they carry the only cue that separates
 * "Remote" the location from "Remote" the workplace type; everything
 * typographic around them is mono, which is where the canvas puts metadata.
 */

const clamp = (lines: number): React.CSSProperties =>
  ({
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  }) as React.CSSProperties;

const metaStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontFamily: "'Manrope', sans-serif",
  fontSize: 12,
  color: "rgba(var(--ink-rgb),.62)",
};

const badgeStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontFamily: "'Manrope', sans-serif",
  fontSize: 10.5,
  letterSpacing: ".1em",
  textTransform: "uppercase",
  borderRadius: 999,
  padding: "4px 11px",
  border: "1px solid rgba(var(--ink-rgb),.3)",
  color: "rgba(var(--ink-rgb),.66)",
};

export function JobCard({
  job,
  featured,
  saved,
  applied = false,
  onToggleSave,
  formatDate,
}: {
  job: JobPostingListItem;
  featured: boolean;
  saved: boolean;
  applied?: boolean;
  onToggleSave: (e: React.MouseEvent) => void;
  formatDate: (d: string) => string;
}) {
  const recent = isRecent(job.createdAt);
  return (
    <Link to={`/job/${job.id}`} style={{ display: "block", color: "inherit" }}>
      <div
        className="dc-h-card"
        style={{
          background: "var(--paper2)",
          border: featured
            ? "1px solid var(--acc2)"
            : "1px solid rgba(var(--ink-rgb),.13)",
          borderRadius: 18,
          padding: featured ? 28 : 22,
          transition: "transform .25s, box-shadow .25s, border-color .25s",
        }}
      >
        {featured && (
          <div
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 11.5,
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: "var(--link)",
              marginBottom: 18,
            }}
          >
            <span className="dc-mark" aria-hidden="true" /> Featured role
          </div>
        )}

        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <span
            aria-hidden="true"
            style={{
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: featured ? 60 : 46,
              height: featured ? 60 : 46,
              borderRadius: 9,
              background: "var(--chip)",
              color: "#9DF1FF",
              fontFamily: "'Manrope', sans-serif",
              fontSize: featured ? 20 : 15,
            }}
          >
            {monogram(job.title)}
          </span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 14,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <h3
                    style={{
                      margin: 0,
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 600,
                      fontSize: featured ? 22 : 18,
                      letterSpacing: "-0.01em",
                      lineHeight: 1.25,
                      ...clamp(3), // up to three lines, so a long title still reads in full on a phone (T101026001)
                    }}
                  >
                    {job.title}
                  </h3>
                  {recent && (
                    <span style={badgeStyle}>
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: "var(--acc2)",
                          animation: "dcv2-pulseDot 2.6s ease-in-out infinite",
                        }}
                      />
                      New
                    </span>
                  )}
                  {applied && (
                    <span
                      style={{
                        ...badgeStyle,
                        borderColor: "var(--acc2)",
                        color: "var(--link)",
                      }}
                    >
                      &#10003; Applied
                    </span>
                  )}
                </div>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 12,
                    color: "rgba(var(--ink-rgb),.62)",
                  }}
                >
                  AI2Aim Technologies
                </p>
              </div>

              <button
                type="button"
                onClick={onToggleSave}
                aria-label={saved ? "Remove from saved jobs" : "Save job"}
                aria-pressed={saved}
                className="dc-h-icon"
                style={{
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  cursor: "pointer",
                  background: "transparent",
                  border: "1px solid rgba(var(--ink-rgb),.16)",
                  color: saved ? "var(--link)" : "rgba(var(--ink-rgb),.5)",
                  transition: "border-color .2s, background .2s, color .2s",
                }}
              >
                {saved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
              </button>
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "8px 20px",
                marginTop: 14,
              }}
            >
              <span style={metaStyle}>
                <MapPin size={13} />
                {job.location}
              </span>
              <span style={metaStyle}>
                <Briefcase size={13} />
                {employmentLabel(job.employmentType)}
              </span>
              <span style={metaStyle}>
                <Clock size={13} />
                {formatDate(job.createdAt)}
              </span>
            </div>

            <p
              style={{
                margin: "14px 0 0",
                fontSize: 14.5,
                color: "rgba(var(--ink-rgb),.66)",
                ...clamp(featured ? 3 : 2),
              }}
            >
              {stripMarkdown(job.description)}
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 18,
              }}
            >
              <Pill tone="cyan">{workplaceLabel(job.workplaceType)}</Pill>
              <Pill tone="purple">{seniorityLabel(job.seniorityLevel)}</Pill>
              <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 16 }}>
                {job.salaryRange && (
                  <span
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontSize: 13,
                      color: "var(--acc2)",
                    }}
                  >
                    {job.salaryRange}
                  </span>
                )}
                <span
                  className="dc-h-link"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "rgba(var(--ink-rgb),.66)",
                    transition: "color .2s",
                  }}
                >
                  View role <span aria-hidden="true">&rarr;</span>
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
